/**
 * 舆图：一张从家门口一路画到城里的草图。
 *
 * 点怎么摆看 GEO（哪处在东南、哪处在西北），线怎么连看 PORTALS（门户指到哪儿）。
 * 两样对得上，才看得出自己站在哪儿、下一步能往哪儿走：
 *   朱砂点 = 你在这儿，墨点 = 走过的地方，空心 = 只听说过的，方框 = 进得去的屋子。
 * 平时缩在右上角，只画自己周围这一片；按 Tab 摊开成全图，点一处还能问出走法。
 */

import { PALETTE, sline, srect, stext, wob } from '../render/sketch.js';
import { SCENES, PORTALS, sceneInfo, unlocked, visible, findRoute, geoOf } from '../data/world.js';
import { Repo } from '../data/schema.js';

const SMALL = { w: 240, h: 152 };
const BIG = { w: 620, h: 420 };
/** 缩略图：一个方位单位折多少像素（只看得见自己这一片） */
const SMALL_SCALE = 26;
const FONT = 'px "LXGW WenKai Lite","KaiTi","STKaiti","KaiTi","Songti SC",serif';

function hashOf(s) {
  let h = 2166136261;
  for (let i = 0; i < String(s).length; i++) { h ^= String(s).charCodeAt(i); h = Math.imul(h, 16777619); }
  return (h >>> 0) % 997;
}

export class MinimapUI {
  constructor(engine, opts = {}) {
    this.engine = engine;
    this.db = engine.db;
    this.canvas = document.getElementById('minimap');
    this.ctx = null;
    this.big = false;
    this.tick = 0;
    this.clock = 0;      // 脚下那圈光晕的呼吸相位
    this.hover = null;
    this.hidden = false;
    if (!this.canvas) return;
    this.resize();
    this.bind();
    this.draw();
  }

  canvasSize() { return this.big ? BIG : SMALL; }

  resize() {
    if (!this.canvas) return;
    const { w, h } = this.canvasSize();
    const dpr = Math.min(2, (typeof window !== 'undefined' && window.devicePixelRatio) || 1);
    this.canvas.style.width = `${w}px`;
    this.canvas.style.height = `${h}px`;
    this.canvas.width = Math.round(w * dpr);
    this.canvas.height = Math.round(h * dpr);
    this.canvas.classList.toggle('big', this.big);
    this.ctx = this.canvas.getContext('2d');
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  toggleBig(next) {
    if (!this.canvas) return;
    this.big = next === undefined ? !this.big : !!next;
    if (!this.big) this.hover = null;
    this.resize();
    this.draw();
  }

  setHidden(on) {
    if (!this.canvas) return;
    this.hidden = !!on;
    this.canvas.style.display = this.hidden ? 'none' : '';
  }

  bind() {
    if (!this.canvas) return;
    this.canvas.addEventListener('click', (e) => {
      const hit = this.pickNode(e);
      if (hit) {
        // 点一处就问路：从自己脚下走到那儿要经过哪些地方
        const here = Repo.getPlayer(this.db).scene;
        const route = hit.id === here ? [here] : findRoute(here, hit.id);
        this.hover = { id: hit.id, info: hit.info, route };
        this.draw();
        return;
      }
      // 摊开时点空白处收回去
      if (this.big) this.toggleBig(false);
    });
    this.canvas.addEventListener('dblclick', (e) => e.stopPropagation());
  }

  pickNode(e) {
    if (!this.canvas || !this.dots) return null;
    const r = this.canvas.getBoundingClientRect();
    const scale = r.width / this.canvasSize().w;          // #ui 有缩放，换回来
    const mx = (e.clientX - r.left) / scale;
    const my = (e.clientY - r.top) / scale;
    if (!this.big) return null;
    for (const d of this.dots) {
      if (Math.hypot(d.x - mx, d.y - my) <= 12) return d;
    }
    return null;
  }

  update(dt) {
    if (!this.canvas) return;
    this.clock += dt;
    this.tick += dt;
    if (this.tick >= 0.2) { this.tick = 0; this.draw(); }
  }

  /** 已知的地方 -> 图上这一点的位置 */
  layout() {
    const db = this.db;
    const known = visible(db);
    const here = Repo.getPlayer(db).scene;
    const nodes = [];
    for (const id of SCENES.keys()) {
      if (!known.has(id)) continue;
      const info = sceneInfo(id);
      const g = info.geo || { x: 0, y: 0 };
      const seed = hashOf(id);
      nodes.push({
        id, info,
        gx: g.x + wob(seed, 0.12),
        gy: g.y + wob(seed + 31, 0.12),
        open: unlocked(db, id),
        isHere: id === here,
        indoor: !!info.indoor,
      });
    }
    // 脚下这一处不在册（比如编辑器新造的图）也要画出来：图上得先有自己
    if (!nodes.some((n) => n.isHere)) {
      const info = sceneInfo(here);
      const g = info.geo || geoOf(here);
      nodes.push({ id: here, info, gx: g.x, gy: g.y, open: true, isHere: true, indoor: !!info.indoor });
    }
    return { nodes, here, known };
  }

  /** 视图：摊开时把整幅装下，缩着时以自己为中心只取一片 */
  view(nodes) {
    const { w, h } = this.canvasSize();
    if (!nodes.length) return { s: SMALL_SCALE, cx: 0, cy: 0 };
    if (this.big) {
      const xs = nodes.map((n) => n.gx), ys = nodes.map((n) => n.gy);
      const minX = Math.min(...xs), maxX = Math.max(...xs);
      const minY = Math.min(...ys), maxY = Math.max(...ys);
      const bw = Math.max(6, maxX - minX), bh = Math.max(6, maxY - minY);
      const s = Math.min((w - 92) / bw, (h - 76) / bh);
      return { s: Math.max(8, Math.min(46, s)), cx: (minX + maxX) / 2, cy: (minY + maxY) / 2 };
    }
    const me = nodes.find((n) => n.isHere) || nodes[0];
    return { s: SMALL_SCALE, cx: me.gx, cy: me.gy };
  }

  draw() {
    const ctx = this.ctx;
    if (!ctx) return;
    const { w, h } = this.canvasSize();
    const big = this.big;
    ctx.clearRect(0, 0, w, h);

    // 纸底
    ctx.fillStyle = PALETTE.paper;
    ctx.globalAlpha = big ? 0.97 : 0.88;
    ctx.fillRect(0, 0, w, h);
    ctx.globalAlpha = 1;
    srect(ctx, 1, 1, w - 2, h - 2, { color: PALETTE.ink, width: 1.4, seed: 11 });

    const { nodes, here, known } = this.layout();
    const { s, cx, cy } = this.view(nodes);
    const ox = w / 2, oy = (big ? (h - 12) / 2 : (h - 14) / 2);
    const px = (n) => ox + (n.gx - cx) * s;
    const py = (n) => oy + (n.gy - cy) * s;
    const inside = (x, y) => x > 6 && x < w - 6 && y > 8 && y < (big ? h - 26 : h - 16);

    const dots = [];
    this.dots = dots;
    for (const n of nodes) {
      const x = px(n), y = py(n);
      n.x = x; n.y = y;
      if (inside(x, y)) dots.push(n);
    }

    const me = nodes.find((n) => n.isHere) || null;
    const neigh = new Set((PORTALS.get(here) || []).map((p) => p.to));

    ctx.save();
    ctx.beginPath();
    ctx.rect(3, 3, w - 6, h - (big ? 24 : 14));
    ctx.clip();

    // 路：先画别人家的，再从自己脚下画出去的那几条
    for (const pass of [0, 1]) {
      for (const [from, list] of PORTALS) {
        const a = nodes.find((n) => n.id === from);
        if (!a || !known.has(from)) continue;
        for (const p of list) {
          const b = nodes.find((n) => n.id === p.to);
          if (!b || !known.has(p.to)) continue;
          const mine = a.id === here || b.id === here;
          if ((pass === 1) !== mine) continue;
          sline(ctx, px(a), py(a), px(b), py(b), {
            color: mine ? PALETTE.ink : PALETTE.inkSoft,
            width: mine ? 1.7 : 1,
            alpha: mine ? 0.55 : 0.28,
            seed: from.length * 7 + p.to.length * 3,
          });
        }
      }
    }

    // 问出来的那条路：从自己脚下走到点中的地方
    if (big && this.hover && this.hover.route && this.hover.route.length > 1) {
      const r = this.hover.route;
      for (let i = 0; i < r.length - 1; i++) {
        const a = nodes.find((n) => n.id === r[i]);
        const b = nodes.find((n) => n.id === r[i + 1]);
        if (!a || !b) continue;
        sline(ctx, px(a), py(a), px(b), py(b), { color: PALETTE.gold, width: 3.2, alpha: 0.75, seed: 90 + i });
      }
    }

    // 点：进得去的屋子画方框，敞地画圆点
    for (const n of dots) {
      const r = n.isHere ? 5 : 4;
      ctx.save();
      ctx.beginPath();
      if (n.indoor) ctx.rect(n.x - r, n.y - r, r * 2, r * 2);
      else ctx.arc(n.x, n.y, r, 0, Math.PI * 2);
      if (n.isHere) { ctx.fillStyle = PALETTE.cloth; ctx.fill(); }
      else if (n.open) { ctx.fillStyle = PALETTE.ink; ctx.globalAlpha = 0.78; ctx.fill(); ctx.globalAlpha = 1; }
      else { ctx.fillStyle = PALETTE.paper; ctx.fill(); }
      ctx.strokeStyle = n.isHere ? PALETTE.cloth : PALETTE.ink;
      ctx.lineWidth = n.isHere ? 1.6 : 1.1;
      ctx.stroke();
      ctx.restore();

      // 地名：全图都写；缩着只写自己与抬脚就到的那几处
      const show = big || n.isHere || neigh.has(n.id);
      if (show) {
        stext(ctx, n.info.name, n.x + (n.indoor ? 8 : 7), n.y + 0.5, {
          font: `${big ? 11.5 : 11}${FONT}`,
          align: 'left',
          alpha: n.isHere ? 1 : (n.open ? 0.9 : 0.5),
          color: n.isHere ? PALETTE.cloth : PALETTE.ink,
        });
      }
    }

    // 你在这里：脚下一圈呼吸的光晕，顶上一个小箭头
    if (me && inside(me.x, me.y)) {
      const pulse = 8 + Math.sin(this.clock * 3.2) * 2.4;
      ctx.save();
      ctx.beginPath();
      ctx.arc(me.x, me.y, pulse, 0, Math.PI * 2);
      ctx.strokeStyle = PALETTE.cloth;
      ctx.globalAlpha = 0.35 + Math.sin(this.clock * 3.2) * 0.12;
      ctx.lineWidth = 1.6;
      ctx.stroke();
      ctx.restore();
      // 箭头指在自己头顶上；头顶快顶到纸边就翻到脚下
      const up = me.y > 34;
      const ax = me.x, ay = me.y + (up ? -8 : 8), at = up ? -7.5 : 7.5;
      ctx.save();
      ctx.fillStyle = PALETTE.cloth;
      ctx.beginPath();
      ctx.moveTo(ax, ay);
      ctx.lineTo(ax - 4.2, ay + at);
      ctx.lineTo(ax + 4.2, ay + at);
      ctx.closePath();
      ctx.fill();
      ctx.restore();
      if (!big) {
        stext(ctx, '你在这里', me.x, me.y + (up ? -21 : 24), { font: `10.5${FONT}`, color: PALETTE.cloth });
      }
    }
    ctx.restore();

    // 角上的题字
    const hereInfo = sceneInfo(here);
    if (big) {
      stext(ctx, '洞天舆图', w / 2, 17, { font: `13${FONT}`, alpha: 0.75 });
      stext(ctx, 'Tab 收起', w - 40, 17, { font: `11${FONT}`, color: PALETTE.inkSoft });
      stext(ctx, `人在：${hereInfo.name}`, 44, h - 12, { font: `11.5${FONT}`, color: PALETTE.cloth, align: 'left' });
      stext(ctx, '● 走过　○ 听说过　▣ 进得去的屋子', w / 2 + 60, h - 12, { font: `10.5${FONT}`, color: PALETTE.inkSoft });
      if (this.hover) {
        const r = this.hover.route;
        const txt = !r
          ? `${this.hover.info.name}：眼下没有路走得通`
          : r.length < 2
            ? `${this.hover.info.name}：你已经在这儿了`
            : `由此去：${r.map((id) => sceneInfo(id).name).join(' → ')}`;
        stext(ctx, txt, w - 12, h - 12, { font: `11${FONT}`, color: PALETTE.inkSoft, align: 'right' });
      }
    } else {
      stext(ctx, `${hereInfo.name}　Tab 展开`, w / 2, h - 9, { font: `10.5${FONT}`, color: PALETTE.inkSoft });
    }
  }
}
